// One cursor state machine for ordinary browsing and indexed search. It never
// infers exhaustion from the number of cards surviving eligibility filtering.
export const QUOTE_REPOSITORY_PAGE_SIZE = 25;
export const QUOTE_REPOSITORY_MAX_PAGES = 3;

export const quoteRepositoryIdentity = record => String(
  record.sourceQuoteId || record.quoteId || record.id || record.quoteIndexId || ""
);

export function quoteRepositoryKey({ workspaceUid, authUid, access, sort, filters, search = "" }) {
  if (!workspaceUid || !authUid) throw new Error("Quotation workspace and user are required.");
  return JSON.stringify([workspaceUid, authUid, access, sort, filters, search]);
}

export function createQuoteRepositoryPager({ key, fetchPage, eligible, identity = quoteRepositoryIdentity }) {
  let generation = 0;
  let disposed = false;
  let state = { key, records: [], cursor: null, exhausted: false, fetched: 0, error: null };
  let pending = null;
  const snapshot = () => ({ ...state, records: [...state.records] });
  return {
    snapshot,
    dispose() { disposed = true; generation += 1; pending = null; },
    async next() {
      if (disposed) return null;
      if (pending) return pending;
      if (state.exhausted) return snapshot();
      const requestGeneration = generation;
      pending = (async () => {
        let cursor = state.cursor;
        let exhausted = false;
        let fetched = 0;
        const additions = new Map();
        try {
          for (let page = 0; page < QUOTE_REPOSITORY_MAX_PAGES; page += 1) {
            const result = await fetchPage(cursor);
            if (requestGeneration !== generation) return null;
            const records = result.records || [];
            fetched += result.rawCount ?? records.length;
            cursor = result.cursor;
            exhausted = !result.hasMore;
            records.filter(eligible).forEach(record => additions.set(identity(record), record));
            if (exhausted || additions.size >= QUOTE_REPOSITORY_PAGE_SIZE) break;
            if (!cursor) throw new Error("Quotation cursor did not advance.");
          }
          const merged = new Map(state.records.map(record => [identity(record), record]));
          additions.forEach((record, id) => { if (id) merged.set(id, record); });
          state = { ...state, records: [...merged.values()], cursor, exhausted,
            fetched: state.fetched + fetched, error: null };
          return snapshot();
        } catch (error) {
          if (requestGeneration !== generation) return null;
          state = { ...state, error };
          throw error;
        } finally {
          if (requestGeneration === generation) pending = null;
        }
      })();
      return pending;
    },
  };
}

// Listing normalization never invents a business timestamp. Missing history is
// accessible in compatibility mode and remains explicitly manual-only.
export function resolveQuoteListingDate(record = {}) {
  for (const source of ["updatedAt", "lastEditedAt", "createdAt", "quoteDate"]) {
    const value = record[source];
    if (typeof value === "string" && !/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value)) continue;
    if (value && typeof value === "object" && !(value instanceof Date) && typeof value.toMillis !== "function") continue;
    const millis = typeof value?.toMillis === "function" ? value.toMillis()
      : value instanceof Date ? value.getTime() : typeof value === "string" ? Date.parse(value) : NaN;
    if (Number.isFinite(millis)) return { millis, source, manualOnly: false };
  }
  return { millis: null, source: "none", manualOnly: true };
}
